/**
 * DatasetDetailPage Component (TASK-288, Epic 29 — Outbound Calling)
 *
 * Schema-driven campaign detail: a progress widget from the `progress`
 * aggregates, a per-state filter that re-queries the records API, a records
 * table whose COLUMNS and CELLS are rendered from the schema (generic — custom
 * fields included), and a do-not-call action. Progress + records poll while the
 * campaign is active (TanStack `refetchInterval`).
 *
 * Visual layer follows the redesigned list pattern: record / progress states
 * rendered as a colored dot + text (not a Badge), a colored title icon, and a
 * primary blue accent on the active state filter.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CopyPlus, Loader2, Phone, PhoneOff, Trash2, Upload } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { PaginationBar } from '@/components/common/PaginationBar';
import { usePageSize } from '@/hooks/usePageSize';
import { NEXT_BATCH_LABEL, useNextBatch } from '@/hooks/useNextBatch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { StatusDot, type StatusDotColor } from '@/components/ui/status-dot';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { recordStateLabel, recordStopAction } from '@/lib/campaignKind';
import { CampaignKindBadge } from '@/components/datasets/CampaignKindBadge';
import { datasetsApi } from '@/services/api/datasets';
import { apiClient } from '@/services/api/client';
import type { SchemaField } from '@/types/dataset';

const POLL_INTERVAL_MS = 5000;

// Dot + text colors (shared StatusDot) for the well-known states; unknown
// states fall back to a neutral gray so the UI stays generic (schema-driven,
// arbitrary states).
const STATE_COLORS: Record<string, StatusDotColor> = {
  new: 'gray',
  queued: 'blue',
  pending: 'yellow',
  contacted: 'green',
  do_not_call: 'red',
  failed: 'red',
  // TASK-413 (AC-5): calling = actively in progress (blue, distinct meaning
  // from "queued"'s waiting-to-start blue is close enough — both are
  // in-flight states, not a semantic clash); no_answer = neutral outcome, not
  // a hard failure like `failed`/`do_not_call` (red is reserved for those);
  // answered = success, same meaning as `contacted` → shares green on purpose.
  calling: 'blue',
  no_answer: 'gray',
  answered: 'green',
  // KAN-593: Telegram outreach states.
  messaged: 'blue',
  replied: 'green',
  not_in_telegram: 'gray',
  // KAN-999: переписка уже идёт, рассылка не писала.
  already_in_dialog: 'gray',
  do_not_contact: 'red',
};

const stateColor = (state: string): StatusDotColor =>
  STATE_COLORS[state] ?? 'gray';

function renderCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

export function DatasetDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // '' = all states.
  const [stateFilter, setStateFilter] = useState<string>('');
  const { page, setPage, pageSize, setPageSize } = usePageSize('dataset-records');

  // TASK-338: delete-campaign confirm dialog visibility.
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    document.title = 'Кампания обзвона - SMETA AI';
  }, []);

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const canManage = currentUser?.permissions?.users?.manage === true;

  const { data: dataset } = useQuery({
    queryKey: ['dataset', id],
    queryFn: () => datasetsApi.get(id),
    enabled: !!id,
  });

  const isActive = dataset?.status === 'active';

  const { data: progress } = useQuery({
    queryKey: ['datasetProgress', id],
    queryFn: () => datasetsApi.progress(id),
    enabled: !!id,
    refetchInterval: isActive ? POLL_INTERVAL_MS : false,
  });

  const { data: recordsData } = useQuery({
    queryKey: ['datasetRecords', id, stateFilter, page, pageSize],
    queryFn: () =>
      datasetsApi.listRecords(id, {
        state: stateFilter || undefined,
        page,
        page_size: pageSize,
      }),
    enabled: !!id,
    refetchInterval: isActive ? POLL_INTERVAL_MS : false,
  });

  // KAN-594: состояние и подпись кнопки задаёт вид кампании.
  const stopAction = recordStopAction(dataset?.kind);

  const setStateMutation = useMutation({
    mutationFn: (recordId: string) =>
      datasetsApi.setRecordState(id, recordId, stopAction.state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasetRecords', id] });
      queryClient.invalidateQueries({ queryKey: ['datasetProgress', id] });
      toast.success(stopAction.successMessage);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Не удалось обновить запись');
    },
  });

  // KAN-850: next batch of this list (empty copy + its upload page).
  const nextBatchMutation = useNextBatch();

  // TASK-338: delete the campaign, then go back to the campaigns list.
  const deleteMutation = useMutation({
    mutationFn: () => datasetsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      setIsDeleteOpen(false);
      toast.success('Кампания удалена');
      navigate('/clients/cold-calls');
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'Не удалось удалить кампанию'));
    },
  });

  const schema: SchemaField[] = useMemo(() => dataset?.schema ?? [], [dataset]);
  const records = recordsData?.results ?? [];

  // Filter buttons: "all" + every state present in the progress breakdown.
  const stateOptions = useMemo(() => {
    const states = progress?.states ? Object.keys(progress.states) : [];
    return states;
  }, [progress]);

  const total = progress?.total ?? dataset?.total ?? 0;
  const contacted = progress?.contacted ?? dataset?.contacted ?? 0;
  const percent = total > 0 ? Math.round((contacted / total) * 100) : 0;

  const handleFilter = useCallback(
    (state: string) => {
      setStateFilter(state);
      setPage(1);
    },
    [setPage]
  );

  return (
    <PageLayout
      data-testid="dataset-detail-page"
      title={dataset?.name ?? 'Кампания'}
      subtitle={
        dataset ? (
          <span className="hidden sm:inline-flex items-center gap-2">
            {dataset.profile_label || dataset.profile_codename}
            <CampaignKindBadge kind={dataset.kind} />
          </span>
        ) : undefined
      }
      titleIcon={
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Phone className="h-5 w-5 text-white" />
        </div>
      }
      backButton={{ href: '/clients/cold-calls' }}
      breadcrumbs={[
        { label: 'Главная', href: '/' },
        { label: 'Контакты', href: '/clients' },
        { label: 'Холодные обзвоны', href: '/clients/cold-calls' },
        { label: dataset?.name ?? 'Кампания' },
      ]}
      actions={
        canManage ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              data-testid="upload-base-button"
              onClick={() => navigate(`/clients/cold-calls/${id}/upload`)}
            >
              <Upload className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Загрузить базу</span>
            </Button>
            <Button
              variant="outline"
              data-testid="next-batch-button"
              title={NEXT_BATCH_LABEL}
              disabled={nextBatchMutation.isPending}
              onClick={() => nextBatchMutation.mutate(id)}
            >
              <CopyPlus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{NEXT_BATCH_LABEL}</span>
            </Button>
            {/* TASK-338: delete the campaign (confirm dialog below). */}
            <Button
              variant="outline"
              data-testid="delete-dataset-button"
              disabled={deleteMutation.isPending}
              onClick={() => setIsDeleteOpen(true)}
            >
              <Trash2 className="h-4 w-4 text-red-500 sm:mr-2" />
              <span className="hidden sm:inline">Удалить кампанию</span>
            </Button>
          </div>
        ) : undefined
      }
    >
      {/* Progress — rendered once the aggregates are loaded. */}
      {progress && (
        <div
          data-testid="dataset-progress"
          className="bg-white border rounded-lg p-4 mb-6 space-y-3"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium text-gray-900">Прогресс</h3>
            <span className="text-sm text-gray-600">
              <strong>{contacted}</strong> из <strong>{total}</strong> (
              {percent}%)
            </span>
          </div>
          <Progress value={percent} />
          <div className="flex flex-wrap gap-x-4 gap-y-2 pt-2">
            {progress?.states &&
              Object.entries(progress.states).map(([state, value]) => (
                <StatusDot
                  key={state}
                  data-testid={`progress-state-${state}`}
                  color={stateColor(state)}
                  label={
                    <>
                      {recordStateLabel(state, dataset?.kind)}:{' '}
                      <strong>{value}</strong>
                    </>
                  }
                />
              ))}
          </div>
        </div>
      )}

      {/* State filter */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button
          variant={stateFilter === '' ? 'default' : 'outline'}
          size="sm"
          data-testid="state-filter-all"
          className={cn(
            stateFilter === '' && 'bg-blue-600 hover:bg-blue-700 text-white'
          )}
          onClick={() => handleFilter('')}
        >
          Все
        </Button>
        {stateOptions.map((state) => (
          <Button
            key={state}
            variant={stateFilter === state ? 'default' : 'outline'}
            size="sm"
            data-testid={`state-filter-${state}`}
            className={cn(
              stateFilter === state &&
                'bg-blue-600 hover:bg-blue-700 text-white'
            )}
            onClick={() => handleFilter(state)}
          >
            {recordStateLabel(state, dataset?.kind)}
          </Button>
        ))}
      </div>

      {/* Records table (schema-driven) — rendered once the schema is known. */}
      {dataset && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <Table data-testid="records-table" aria-label="Records table">
            <TableHeader>
              <TableRow>
                {schema.map((field) => (
                  <TableHead key={field.name}>
                    {field.label || field.name}
                  </TableHead>
                ))}
                <TableHead>Состояние</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow
                  key={record.id}
                  data-testid={`record-row-${record.id}`}
                >
                  {schema.map((field) => (
                    <TableCell key={field.name}>
                      {renderCell(record.fields?.[field.name])}
                    </TableCell>
                  ))}
                  <TableCell>
                    <StatusDot
                      color={stateColor(record.state)}
                      label={recordStateLabel(record.state, dataset?.kind)}
                    />
                  </TableCell>
                  <TableCell>
                    {canManage && record.state !== stopAction.state && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`do-not-call-${record.id}`}
                          disabled={setStateMutation.isPending}
                          onClick={() => setStateMutation.mutate(record.id)}
                        >
                          <PhoneOff className="h-4 w-4 sm:mr-1 text-gray-400 hover:text-red-500" />
                          <span className="hidden sm:inline">{stopAction.label}</span>
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              ))}
              {records.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={schema.length + 2}
                    className="text-center text-gray-500 py-8"
                  >
                    Записей нет
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {dataset && recordsData && (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          count={recordsData.count}
          hasPrevious={Boolean(recordsData.previous)}
          hasNext={Boolean(recordsData.next)}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          className="px-0"
        />
      )}

      {/* TASK-338: confirm campaign deletion */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent data-testid="delete-dataset-dialog">
          <DialogHeader>
            <DialogTitle>Удалить кампанию?</DialogTitle>
            <DialogDescription>
              Кампания «{dataset?.name ?? ''}» и все её записи (
              {dataset?.total ?? 0}) будут удалены. Это действие необратимо.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Отмена
            </Button>
            <Button
              data-testid="confirm-delete-button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

export default DatasetDetailPage;
